import { useEffect, useState } from 'react';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { ksdpMonitoringEditPermission } from '@internal/ksdp-permissions-common';
import { formatDateTime } from './formatDateTime';
import { MonitoringRecord } from './types';

export interface MonitoringViewProps {
  ban: string;
  /** application_data.owner as returned by GET /applications/{ban} — used
   * to gate the "security changes since onboarding" question to only the
   * application's owner. See the ownership-check note below. */
  owner: string | null | undefined;
  onBack: () => void;
}

const ONBOARDING_STATUSES = ['Not Onboarded', 'Onboarding', 'Onboarded'];

function formatAuditTimestamp(value: string | null): string {
  if (!value) return 'N/A';
  return formatDateTime(value);
}

function formatAuditActor(value: string | null): string {
  if (!value) return 'N/A';
  return value.replace(/^user:default\//, '');
}

/**
 * Monitoring module view — submit/edit workflow backed by the real
 * ksdp-get-monitoring / ksdp-upsert-monitoring Lambdas (own dedicated
 * `monitoring` Postgres schema — see DB_schema.md section 6).
 *
 * Redesigned per product decision (see MonitoringView.tsx's prior revision
 * for the earlier single-flat-form version):
 *   - Progressive disclosure: Onboarding Status is the only field shown
 *     initially. Onboarding Date only appears once "Onboarded" is
 *     selected. The old standalone "Request a security review" checkbox
 *     is REMOVED entirely — it was unrelated UI clutter with no connection
 *     to the rest of the form.
 *   - A new yes/no question ("has there been any major security changes
 *     since last onboarding of this application?") appears ONLY once the
 *     6-month re-validation reminder has actually been sent for this BAN
 *     (record.validation_email_sent — see the new monitoring_reminder
 *     Lambda) AND only to the application's owner (see the ownership
 *     check below). Answering "Yes" IS the security-review request now —
 *     upsert_monitoring derives security_review_requested from this
 *     answer server-side and sends the review-needed email itself; there
 *     is no separate action here.
 *
 * Per product decision: a single Submit button commits the current form
 * state; after a successful save the form becomes read-only and an Edit
 * button appears to re-enter edit mode (re-showing Submit).
 *
 * Ownership check: compares the signed-in user's email (via
 * IdentityApi.getProfileInfo()) against application_data.owner. This is a
 * best-effort, CLIENT-SIDE-ONLY convenience gate, not a security boundary —
 * DB_schema.md explicitly flags that `owner`'s exact format (email vs.
 * ServiceNow sys_id vs. display name) has never been confirmed against the
 * ServiceNow field mapping, so a mismatch here (e.g. owner stored as a
 * sys_id) will simply keep the question hidden for the real owner rather
 * than granting/denying access incorrectly. upsert_monitoring's own
 * server-side gate (validation_email_sent) is the actual enforcement for
 * *when* an answer can be submitted at all; there is no server-side
 * check of *who* is submitting it yet, which would require resolving this
 * same owner-format ambiguity first.
 */
export function MonitoringView({ ban, owner, onBack }: MonitoringViewProps) {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  // UI-only gate; POST /monitoring/{ban} is enforced by the backend router.
  const { allowed: canEdit } = usePermission({
    permission: ksdpMonitoringEditPermission,
  });

  const [record, setRecord] = useState<MonitoringRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);

  // Form state — only populated fields the user can actually edit.
  const [onboardingStatus, setOnboardingStatus] = useState<string>('');
  const [onboardingDate, setOnboardingDate] = useState<string>('');
  const [ownerResponse, setOwnerResponse] = useState<string>('');

  useEffect(() => {
    identityApi
      .getProfileInfo()
      .then(profile => setCurrentUserEmail(profile.email ?? null))
      .catch(() => setCurrentUserEmail(null));
  }, [identityApi]);

  const requiresDate = onboardingStatus === 'Onboarded';

  // Best-effort, case-insensitive comparison — see the ownership-check note
  // in this component's docstring for why this cannot be a hard security
  // boundary yet.
  const isOwner = Boolean(
    currentUserEmail &&
      owner &&
      currentUserEmail.toLowerCase() === owner.toLowerCase(),
  );
  const reminderSent = Boolean(record?.validation_email_sent);
  const canAnswerSecurityChangeQuestion =
    requiresDate && reminderSent && isOwner;

  // Without edit rights a BAN with no record is shown read-only instead of
  // opening straight into the edit form.
  const showEditor = editing && canEdit;

  const canSubmit =
    Boolean(onboardingStatus) &&
    (!requiresDate || Boolean(onboardingDate)) &&
    (!canAnswerSecurityChangeQuestion || Boolean(ownerResponse));

  const loadRecord = async () => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('business-applications');
      const res = await fetchApi.fetch(`${baseUrl}/monitoring/${ban}`);
      if (!res.ok) {
        throw new Error(`Failed to load monitoring record (${res.status})`);
      }
      const data: MonitoringRecord = await res.json();
      setRecord(data);
      setOnboardingStatus(data.onboarding_status ?? '');
      setOnboardingDate(
        data.onboarding_date ? data.onboarding_date.slice(0, 10) : '',
      );
      setOwnerResponse(data.owner_response ?? '');
      // A BAN with no prior submission (all fields null) opens straight
      // into edit mode; an existing record opens read-only.
      setEditing(!data.onboarding_status);
    } catch {
      setError('Could not load monitoring information for this application.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ban]);

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    setJustSaved(false);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('business-applications');
      const res = await fetchApi.fetch(`${baseUrl}/monitoring/${ban}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          onboarding_status: onboardingStatus || undefined,
          onboarding_date:
            onboardingStatus === 'Onboarded' ? onboardingDate : undefined,
          owner_response: canAnswerSecurityChangeQuestion
            ? ownerResponse || undefined
            : undefined,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to save (${res.status})`);
      }
      await loadRecord();
      setEditing(false);
      setJustSaved(true);
    } catch (err: any) {
      setError(err?.message || 'Could not save monitoring information.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card detail-view-card">
      <div className="detail-view-header">
        <button type="button" className="back-button" onClick={onBack}>
          <span aria-hidden="true">&#8592;</span> Back to Application Modules
        </button>
        <h4 className="detail-view-title">Monitoring</h4>
      </div>

      {loading && <p className="loading-text">Loading...</p>}

      {!loading && error && <p className="monitoring-error-notice">{error}</p>}

      {!loading && !showEditor && record && (
        <div>
          <dl>
            <div className="monitoring-readonly-field">
              <dt>Onboarding Status:</dt>
              <dd>{record.onboarding_status ?? 'N/A'}</dd>
            </div>
            {record.onboarding_status === 'Onboarded' && (
              <div className="monitoring-readonly-field">
                <dt>Onboarding Date:</dt>
                <dd>
                  {record.onboarding_date
                    ? record.onboarding_date.slice(0, 10)
                    : 'N/A'}
                </dd>
              </div>
            )}
            {canAnswerSecurityChangeQuestion && record.owner_response && (
              <div className="monitoring-readonly-field">
                <dt>Security changes since onboarding:</dt>
                <dd>{record.owner_response}</dd>
              </div>
            )}
            <div className="monitoring-readonly-field">
              <dt>Created By:</dt>
              <dd>{formatAuditActor(record.created_by)}</dd>
            </div>
            <div className="monitoring-readonly-field">
              <dt>Updated By:</dt>
              <dd>{formatAuditActor(record.updated_by)}</dd>
            </div>
            <div className="monitoring-readonly-field">
              <dt>Updated At:</dt>
              <dd>{formatAuditTimestamp(record.updated_at)}</dd>
            </div>
          </dl>

          {justSaved && <p className="monitoring-saved-notice">Saved.</p>}

          {canEdit && (
            <div className="inline-actions">
              <button
                type="button"
                className="action-button"
                onClick={() => {
                  setEditing(true);
                  setJustSaved(false);
                }}
              >
                Edit
              </button>
            </div>
          )}
        </div>
      )}

      {!loading && showEditor && (
        <div>
          <div className="monitoring-field">
            <label
              className="monitoring-label"
              htmlFor="onboarding-status-select"
            >
              Onboarding Status
            </label>
            <select
              className="form-control"
              id="onboarding-status-select"
              value={onboardingStatus}
              onChange={e => setOnboardingStatus(e.target.value)}
            >
              <option value="" disabled>
                Select a status
              </option>
              {ONBOARDING_STATUSES.map(status => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          {requiresDate && (
            <div className="monitoring-field">
              <label
                className="monitoring-label"
                htmlFor="onboarding-date-input"
              >
                Onboarding Date
              </label>
              <input
                type="date"
                className="form-control"
                id="onboarding-date-input"
                value={onboardingDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={e => setOnboardingDate(e.target.value)}
              />
            </div>
          )}

          {/* Only shown once the 6-month re-validation email has actually
              been sent for this BAN (record.validation_email_sent — set by
              the monitoring_reminder Lambda) AND to the application's
              owner — see this component's docstring for the ownership
              caveat. */}
          {canAnswerSecurityChangeQuestion && (
            <div className="monitoring-field">
              <label
                className="monitoring-label"
                htmlFor="owner-response-select"
              >
                Has there been any major security changes since last onboarding
                of this application?
              </label>
              <select
                className="form-control"
                id="owner-response-select"
                value={ownerResponse}
                onChange={e => setOwnerResponse(e.target.value)}
              >
                <option value="" disabled>
                  Select an answer
                </option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          )}

          {error && <p className="monitoring-error-notice">{error}</p>}

          <div className="inline-actions">
            <button
              type="button"
              className="action-button"
              disabled={!canSubmit || saving}
              onClick={handleSubmit}
            >
              {saving ? 'Saving...' : 'Submit'}
            </button>
            {record?.onboarding_status && (
              <button
                type="button"
                className="action-button secondary-button"
                disabled={saving}
                onClick={() => {
                  setEditing(false);
                  setError(null);
                  // Revert any unsaved edits back to the last persisted values.
                  setOnboardingStatus(record.onboarding_status ?? '');
                  setOnboardingDate(
                    record.onboarding_date
                      ? record.onboarding_date.slice(0, 10)
                      : '',
                  );
                  setOwnerResponse(record.owner_response ?? '');
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
